/**
 * Shared optimistic list updates for React Query + IndexedDB (offline reads).
 */
import { buildFullApiCacheKey } from '../cache/cache-utils.js';
import { getUser } from '../../services/storage/localStorage.service.js';

const defaultPagination = (totalItems = 0) => ({
  currentPage: 1,
  totalPages: 1,
  totalItems,
  hasNextPage: false,
  hasPrevPage: false,
});

export const readStoredPageSize = (storageKey, fallback = 20) => {
  try {
    const saved = localStorage.getItem(`gym-page-size-${storageKey}`);
    if (saved === 'all') return 1000;
    const parsed = parseInt(saved, 10);
    return !Number.isNaN(parsed) && parsed > 0 ? parsed : fallback;
  } catch {
    return fallback;
  }
};

export const getRequestParamsFromQueryKey = (queryKey) => {
  const params = {};

  for (let i = 1; i < queryKey.length; i += 1) {
    const item = queryKey[i];
    if (typeof item === 'number') {
      if (params.page === undefined) params.page = item;
      else if (params.limit === undefined) params.limit = item;
    } else if (item && typeof item === 'object' && !Array.isArray(item)) {
      Object.assign(params, item);
    }
  }

  return params;
};

/** Likely list query keys when no cache entry exists yet (e.g. create from modal). */
export function getDefaultListQueryKeys(queryKeyRoot, pageSizeStorageKey, extraVariants = []) {
  const limit = readStoredPageSize(pageSizeStorageKey);
  const keys = [
    [...queryKeyRoot, 1, limit, {}],
    ...extraVariants.map((variant) => [...queryKeyRoot, 1, limit, variant]),
  ];

  const user = getUser();
  if (user?.role === 'trainer') {
    const trainerId = user.trainerId || user._id || user.userId;
    if (trainerId) {
      keys.push([...queryKeyRoot, 1, limit, { trainerId }]);
      keys.push([...queryKeyRoot, 'my-athletes', { trainerId, page: 1, limit }]);
    }
  }

  return keys;
};

/**
 * @param {object} config
 * @param {string} config.listKey - e.g. 'athletes', 'trainers'
 * @param {string} config.showEndpoint - API_CONFIG path for list GET
 * @param {object} config.repository - IndexedDB repository with saveResponse
 * @param {(queryKey: unknown[]) => boolean} config.isListQuery
 * @param {readonly unknown[]} config.queryKeyRoot
 * @param {string} [config.pageSizeStorageKey]
 * @param {Array<Record<string, unknown>>} [config.extraQueryVariants]
 */
export function createOptimisticListHelpers(config) {
  const {
    listKey,
    showEndpoint,
    repository,
    isListQuery,
    queryKeyRoot,
    pageSizeStorageKey,
    extraQueryVariants = [],
  } = config;

  const persistListQuery = async (queryKey, data) => {
    const params = getRequestParamsFromQueryKey(queryKey);
    const cacheKey = buildFullApiCacheKey(showEndpoint, params);
    await repository.saveResponse(cacheKey, data, {
      ttl: 7 * 24 * 60 * 60 * 1000,
    }).catch((err) => {
      console.error(`[${listKey}] Failed to persist optimistic list:`, err);
    });
    // Also save a broad (no-params) copy so offline fallback in useQuery hooks
    // (which looks up with `{}` params) can find the optimistic data regardless
    // of which query params the current page uses.
    // IMPORTANT: Only save to the broad key for base (unpaginated, unfiltered)
    // queries.  Paginated (page 1, 2, …), search, my-athletes and list-filtered
    // queries each hold a subset of the data; saving them to the broad key would
    // overwrite the full dataset (e.g. 100 000 athletes) with just 20 items,
    // breaking offline search, page 2+ data and the member overview cards.
    const qParams = getRequestParamsFromQueryKey(queryKey);
    const isBaseQuery = !qParams.page && !qParams.limit && queryKey.length <= 1;
    if (isBaseQuery) {
      const broadKey = buildFullApiCacheKey(showEndpoint, {});
      if (broadKey !== cacheKey) {
        await repository.saveResponse(broadKey, data, {
          ttl: 7 * 24 * 60 * 60 * 1000,
        }).catch(() => {});
      }
    }
  };

  const buildListPayload = (items, oldData) => {
    if (Array.isArray(oldData)) return items;
    return {
      ...(oldData && typeof oldData === 'object' ? oldData : {}),
      [listKey]: items,
      pagination: {
        ...(oldData?.pagination || defaultPagination(items.length)),
        totalItems: items.length,
      },
    };
  };

  const applyToQuery = (queryClient, queryKey, oldData, updater) => {
    if (!isListQuery(queryKey)) return;

    if (oldData == null) {
      const items = updater([]);
      const updated = buildListPayload(items, null);
      queryClient.setQueryData(queryKey, updated);
      // Only persist to IndexedDB if the updater actually produced items
      // (e.g. CREATE adds a new item to the list). For UPDATE/DELETE on an
      // empty list, the result is empty — persisting would overwrite the
      // existing IndexedDB cache with an empty array.
      if (items.length > 0) {
        return persistListQuery(queryKey, updated);
      }
      return Promise.resolve();
    }

    if (Array.isArray(oldData)) {
      const updated = updater(oldData);
      queryClient.setQueryData(queryKey, updated);
      return persistListQuery(queryKey, updated);
    }

    if (Array.isArray(oldData[listKey])) {
      const items = updater(oldData[listKey]);
      const updated = buildListPayload(items, oldData);
      queryClient.setQueryData(queryKey, updated);
      return persistListQuery(queryKey, updated);
    }

    return Promise.resolve();
  };

  const applyListUpdate = (queryClient, updater) => {
    const queries = queryClient.getQueriesData({ queryKey: queryKeyRoot });
    const persistTasks = [];

    queries.forEach(([queryKey, oldData]) => {
      persistTasks.push(applyToQuery(queryClient, queryKey, oldData, updater));
    });

    if (queries.length === 0 && pageSizeStorageKey) {
      getDefaultListQueryKeys(queryKeyRoot, pageSizeStorageKey, extraQueryVariants).forEach(
        (queryKey) => {
          persistTasks.push(applyToQuery(queryClient, queryKey, null, updater));
        }
      );
    }

    return Promise.all(persistTasks);
  };

  const restoreListQueries = (queryClient, entries) => {
    entries?.forEach(([queryKey, data]) => {
      queryClient.setQueryData(queryKey, data);
    });
  };

  const snapshotListQueries = (queryClient) =>
    queryClient.getQueriesData({ queryKey: queryKeyRoot });

  const mergeItemIntoList = (list, item, { replaceTempPrefix = 'temp-' } = {}) => {
    if (!item) return list || [];
    const id = item._id || item.id;
    const withoutTemp = (list || []).filter(Boolean).filter(
      (row) => !String(row._id || '').startsWith(replaceTempPrefix)
    );
    const idx = withoutTemp.findIndex((row) => (row._id || row.id) === id);
    const merged = { ...item, isPending: false };
    if (idx >= 0) {
      const next = [...withoutTemp];
      next[idx] = { ...next[idx], ...merged };
      return next;
    }
    return [...withoutTemp, merged];
  };

  /** Keep optimistic cache + IndexedDB in sync when mutation is queued offline. */
  const persistQueuedListState = (queryClient) => {
    const tasks = [];
    queryClient.getQueriesData({ queryKey: queryKeyRoot }).forEach(([queryKey, data]) => {
      if (isListQuery(queryKey) && data != null) {
        tasks.push(persistListQuery(queryKey, data));
      }
    });
    return Promise.all(tasks);
  };

  return {
    applyListUpdate,
    restoreListQueries,
    snapshotListQueries,
    persistListQuery,
    mergeItemIntoList,
    buildListPayload,
    persistQueuedListState,
    prependListItem: (list, item) => [item, ...list],
  };
}

/** Prepend a new item to list caches (newest first in tables). */
export const prependListItem = (list, item) => [item, ...list];

/** After a successful online mutation, avoid refetch that overwrites optimistic cache with stale IndexedDB. */
export const softInvalidateList = (queryClient, queryKey) => {
  queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
};
